import { stringify } from 'query-string';
import { flattenProducts } from '@fff-web/fff-cart';
import type { Sort } from '../../sharedTypes';
import { campaignId, getCampaignType } from '../../utils/campaign';
import env from '../../utils/env';
import request from './request';
import requestJson from './requestJson';

export function requestProducts(
  offset: number | null,
  selectedCategoryId: string | null,
  sort: Sort,
  shareUrl: string | null,
) {
  const type = getCampaignType();
  let requestUrl = `${env.aceApi}api/1/campaigns/products?type=${type}&orderBy=${sort}`;
  if (offset !== null) {
    requestUrl += `&offset=${offset}&limit=24`;
  }
  if (selectedCategoryId !== null) {
    requestUrl += `&categoryId=${selectedCategoryId}`;
  }
  if (shareUrl !== null) {
    requestUrl += `&extraProductShareUrl=${shareUrl}`;
  }
  if (campaignId !== null) {
    requestUrl += `&campaignId=${campaignId}`;
  }
  return request(requestUrl);
}

export function requestFavoriteProducts(sort) {
  const type = getCampaignType();
  let requestUrl = `${env.aceApi}api/1/favorites/products?type=${type}&orderBy=${sort}`;
  if (campaignId !== null) {
    requestUrl += `&campaignId=${campaignId}`;
  }
  return request(requestUrl);
}

export async function getProductsById(productIds: string[]) {
  const type = getCampaignType();
  const queryParams: Record<string, string> = {
    type,
  };
  if (campaignId) {
    queryParams.campaignId = campaignId;
  }
  const requestUrl = `${env.aceApi}api/1/campaigns/products?${stringify(queryParams)}`;
  const response = await request(
    requestUrl,
    'POST',
    { 'Content-Type': 'application/json' },
    JSON.stringify(productIds),
  );
  const {
    products: productsWithVariants,
    productIds: responseIds,
    totalProductCount,
  } = response.value;
  const { products, variants } = flattenProducts(productsWithVariants);
  return {
    productIds: responseIds,
    products,
    variants,
    totalProductCount,
  };
}

export async function getProducts(
  offset: number | null,
  selectedCategoryId: string | null,
  sort: Sort,
  shareUrl: string | null,
) {
  const productResponse = await requestProducts(offset, selectedCategoryId, sort, shareUrl);
  const { products: productsWithVariants, productIds, totalProductCount } = productResponse.value;
  const { products, variants } = flattenProducts(productsWithVariants);

  return {
    productIds,
    products,
    variants,
    totalProductCount,
  };
}

export async function getBestSellingProducts(
  selectedCategoryId: string | null,
  shareUrl: string | null,
  selectedSort: Sort,
  productRecAbKey: string,
) {
  const campaignType = getCampaignType();
  const queryParams: Record<string, string | number> = {
    campaignType,
    offset: 0,
    orderBy: selectedSort,
    limit: 24,
    abTestKey: productRecAbKey,
  };
  if (campaignId) {
    queryParams.campaignId = campaignId;
  }
  if (selectedCategoryId) {
    queryParams.categoryId = selectedCategoryId;
  }
  if (shareUrl) {
    queryParams.extraProductShareUrl = shareUrl;
  }
  const url = `${env.aceApi}api/1/products?${stringify(queryParams)}`;
  const headers = {
    Accept: 'application/vnd.fabfitfun.v3+json',
  };
  const response = await request(url, 'GET', headers);
  const {
    data: { items },
  } = response;
  const {
    products: productsWithVariants,
    totalProductCount,
    productIds,
    sortedProductIds,
  } = items[0];
  const { products, variants } = flattenProducts(productsWithVariants);
  return {
    productIds,
    sortedProductIds,
    totalProductCount,
    products,
    variants,
  };
}

export async function getFavoriteProducts(sort: Sort) {
  const productResponse = await requestFavoriteProducts(sort);
  const { products: productsWithVariants, productIds, totalProductCount } = productResponse;
  const { products, variants } = flattenProducts(productsWithVariants);
  return {
    productIds,
    products,
    variants,
    totalProductCount,
  };
}

function requestSponsoredProducts() {
  const type = getCampaignType();
  let requestUrl = `${env.aceApi}api/1/campaigns/products?type=${type}&isSponsored=true`;

  if (campaignId !== null) {
    requestUrl += `&campaignId=${campaignId}`;
  }

  return request(requestUrl);
}

export async function getSponsoredProducts() {
  const productResponse = await requestSponsoredProducts();
  const { products: productsWithVariants, productIds, totalProductCount } = productResponse.value;
  const { products, variants } = flattenProducts(productsWithVariants);

  return {
    productIds,
    products,
    variants,
    totalProductCount,
  };
}

export const getProductById = (productId: string) =>
  request(`${env.aceApi}api/1/products/${productId}`);

export function getFavoritesData() {
  const type = getCampaignType();
  let requestUrl = `${env.aceApi}api/1/favorites/products/ids?type=${type}`;
  if (campaignId !== null) {
    requestUrl += `&campaignId=${campaignId}`;
  }
  return request(requestUrl);
}

export function updateFavorite(campaignIdNumber: number, productId: string, liked: boolean) {
  const requestUrl = `${
    env.aceApi
  }api/1/favorites/campaigns/${campaignIdNumber}/products/${productId}/likes?isLike=${
    liked ? 'true' : 'false'
  }`;
  return request(requestUrl, 'PUT');
}

export const getProductsByCampaignType = (campaignType: string) => {
  const type = getCampaignType();
  const headers = { Accept: 'application/vnd.fabfitfun.v3+json' };
  const requestUrl = `${env.aceApi}api/1/products?campaignType=${campaignType || type}`;
  return requestJson(requestUrl, 'GET', headers);
};
